<template>
  <q-page class="dailyReportPage">
    <DailyReportToolBar v-if="!isPrintMode" />
    <transition
      enter-active-class="animated animate__fadeIn"
      leave-active-class="animated animate__fadeOut"
      mode="out-in"
      :duration="140"
    >
      <q-scroll-area
        v-if="!loading"
        :visible="!isPrintMode"
        :style="{
          height: isPrintMode ? '0px' : 'calc(100vh - 183px)',
          minWidth: '100%',
        }"
      >
        <teleport to="#print" :disabled="!isPrintMode">
          <h4 class="q-pl-md" style="margin-bottom: 0" v-if="isPrintMode">
            Dygnsrapport
          </h4>
          <h6 class="q-pl-md" style="margin-top: 0" v-if="isPrintMode">
            {{ date }}
          </h6>
          <div class="row" :style="{ display: isPrintMode ? 'block' : 'flex' }">
            <div class="col-12 col-xl-6">
              <DailyReportTrafficTable />
            </div>
            <div class="col-12 col-xl-6">
              <DailyReportVehicleStatusTable
                :summary="withdrawalDashboardData.summary"
              />
            </div>
          </div>

          <DailyReportEventTable v-if="!isPrintMode" />
          <DailyReportDeviationTable
            v-for="(item, $i) in filteredDeviations"
            :key="$i"
            :title="item.name"
            :data="item.deviations"
          />
        </teleport>
      </q-scroll-area>
      <div
        v-else
        style="height: calc(100vh - 183px)"
        class="full-width items-center justify-center flex"
      >
        <q-spinner color="secondary" size="10em" :thickness="1" />
      </div>
    </transition>
  </q-page>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import DailyReportToolBar from '@/components/daily-report/DailyReportToolBar.vue'
import { useDailyReport } from '@/composable/useDailyReport'
import DailyReportTrafficTable from '@/components/daily-report/DailyReportTrafficTable.vue'
import DailyReportVehicleStatusTable from '@/components/daily-report/DailyReportVehicleStatusTable.vue'
import DailyReportDeviationTable from '@/components/daily-report/DailyReportDeviationTable.vue'
import DailyReportEventTable from '@/components/daily-report/DailyReportEventTable.vue'
import { useVehicle } from '@/composable/useVehicle'
import { useWithdrawalDashboard } from '@/composable/useWithdrawalDashboard'
import { DailyReportDeviation } from '@/types/daily-report-deviation'
import { useEvent } from '@/composable/useEvent'

export default defineComponent({
  name: 'DailyReport',

  components: {
    DailyReportToolBar,
    DailyReportTrafficTable,
    DailyReportVehicleStatusTable,
    DailyReportDeviationTable,
    DailyReportEventTable,
  },

  setup() {
    const {
      loading: dailyReportLoadingState,
      data,
      date,
      isPrintMode,
      selectedFilterEventType,
    } = useDailyReport()
    const { loading: eventLoading } = useEvent()
    const { loading: loadingStateVehicle, fetchAll: fetchAllVehicle } =
      useVehicle()
    const {
      loading: withdrawalDashboardLoading,
      data: withdrawalDashboardData,
    } = useWithdrawalDashboard()

    fetchAllVehicle()

    const loading = computed(() => {
      return (
        eventLoading.value.getAll ||
        dailyReportLoadingState.value.traffic.getAll ||
        dailyReportLoadingState.value.vehicleStatus.getAll ||
        dailyReportLoadingState.value.deviation.getAll ||
        dailyReportLoadingState.value.event.getAll ||
        dailyReportLoadingState.value.monthlySummary.getAll ||
        loadingStateVehicle.value.getAll ||
        withdrawalDashboardLoading.value.fetchSummary
      )
    })

    const trackHasComment = (trackName: string) => {
      return data.value.event.some(
        (x) =>
          x.luppTracks.some((y) => y.description === trackName) && x.description
      )
    }

    const filterDeviation = (deviation: DailyReportDeviation) => {
      return Boolean(
        !deviation.track ||
          trackHasComment(deviation.track) ||
          selectedFilterEventType.value.includes(deviation.type)
      )
    }

    const filterDeviation2 = (deviation: DailyReportDeviation) => {
      return Boolean(
        !deviation.track ||
          selectedFilterEventType.value.includes(deviation.type)
      )
    }

    const filteredDeviations = computed(() =>
      data.value.deviation
        .filter((deviation) => {
          return (
            deviation.deviations.filter(filterDeviation).length ||
            trackHasComment(deviation.name)
          )
        })
        .map((x) => ({
          ...x,
          deviations: x.deviations.filter(filterDeviation2),
        }))
    )

    return {
      date,
      filteredDeviations,
      data,
      loading,
      isPrintMode,
      withdrawalDashboardData,
    }
  },
})
</script>

<style lang="scss">
.dailyReportPrintMode {
  position: fixed;
  top: 0;
  left: 0;
  background: white;
  height: 100%;
  width: 100%;
  z-index: 9999;
  overflow-y: scroll;
  overflow-x: scroll;
}

.printMode .dailyReportPage {
  min-height: 0 !important;
}

.printMode .q-scrollarea__thumb {
  opacity: 0 !important;
}
</style>
